import { API_CALL, SET_CUSTOMER_VIEW_TOGGLE } from 'actions/actionConstants'
import requestGetAccountAgent from 'agentHub/api/requestGetAccountAgent'
import { FETCH_ACCOUNT_AGENT_HUB, FETCH_AGENT_COMMISSION_CONFIG, EMAIL_CHECK_AGENT_STATUS, ONBOARD_AGENCY } from './apiActionConstants'
import { getPromoDiscountsByName } from 'api/promo'
import { arrayToObject } from 'lib/array/arrayUtils'
import requestGetAgentAccountById from 'agentHub/api/requestGetAgentAccountById'
import requestGetAgentStatusByEmail from 'agentHub/api/requestGetAgentStatusByEmail'
import requestPostSetAgencyDetails, { SetAgencyDetailsParams } from 'agentHub/api/requestPostSetAgencyDetails'
import requestPostMarkAgentAsOnboarded from 'agentHub/api/requestPostMarkAgentAsOnboarded'

/**
 * Fetches the agent hub account for the current user
 *
 * Note: Will only work for onboarded users
 */
export function fetchAccountAgentHub(accessToken?: string) {
  return (dispatch, getState) => {
    const state = getState() as App.State

    if (state.agentHub.agentHubAccount.account?.status === 'onboarded') {
      // already have the onboarded version, no need to refetch
      // (we might have the pending version, so if that's the case, refetch it)
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_ACCOUNT_AGENT_HUB,
      request: () => requestGetAccountAgent(accessToken),
    })
  }
}

/**
 * Fetches the agent hub account for the agent given.
 *
 * Note: Will work for accounts that are still pending onboarding
 * @param agentId The ID of the agent to fetch the account for
 */
export function fetchAgentAccountByAgentId(agentId: string) {
  return (dispatch, getState) => {
    const state = getState() as App.State

    if (state.agentHub.agentHubAccount.account?.agentId === agentId) {
      // already have the current agents data, no need to refetch
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_ACCOUNT_AGENT_HUB,
      request: () => requestGetAgentAccountById(agentId),
    })
  }
}

const promoProductToAppProductMap = new Map<App.PromotionDiscountProduct, Array<App.ProductType>>([
  ['ALL-ALL', []],
  ['car_hire-ALL', ['car_hire']],
  ['cruise-ALL', ['cruise', 'cruise-exclusive', 'cruise-flash', 'cruise-tactical']],
  ['experience-ALL', ['experience']],
  ['experience-experience', ['experience']],
  ['hotel-ALL', ['hotel', 'bedbank_hotel', 'tactical_ao_hotel', 'last_minute_hotel', 'ultralux_hotel', 'rental']],
  ['hotel-bedbank_hotel', ['bedbank_hotel']],
  ['hotel-hotel', ['hotel']],
  ['hotel-last_minute_hotel', ['last_minute_hotel']],
  ['hotel-tactical_ao_hotel', ['tactical_ao_hotel']],
  ['insurance-ALL', []],
  ['tour-ALL', ['direct_tour', 'partner_tour', 'connection_tour', 'tour', 'ultra_lux_tour']],
  ['tour-cruise', ['cruise-flash']],
  ['tour-tour', ['direct_tour', 'partner_tour', 'tour', 'ultra_lux_tour']],
  ['tour-tourbank', ['connection_tour']],
])

const AgentPromoCode = 'AGENTHUB001'
export function fetchAgentHubCommissionConfig() {
  return (dispatch, getState) => {
    const state = getState() as App.State

    dispatch({
      type: API_CALL,
      api: FETCH_AGENT_COMMISSION_CONFIG,
      request: () => getPromoDiscountsByName(AgentPromoCode, state.geo.currentRegionCode).then(discounts => {
        const commissionsByProductType = discounts.flatMap(discount =>
          discount.products.flatMap(product =>
            promoProductToAppProductMap.get(product)?.map(productType => ({
              productType,
              value: discount.value,
            })) ?? [],
          ),
        )

        // create our comission config response here
        const config: App.AgentHubCommissionConfig = {
          code: AgentPromoCode,
          commissionAmounts: arrayToObject(
            commissionsByProductType,
            commission => commission.productType,
            commission => commission.value,
          ) as Record<App.ProductType, number>,
        }

        return config
      }),
    })
  }
}

export function checkAgentHubStatus(email: string) {
  return (dispatch, getState) => {
    const state = getState() as App.State

    if (state.agentHub.agentStatusByEmail[email] !== undefined) {
      // already checked it
      return
    }
    dispatch({
      type: API_CALL,
      api: EMAIL_CHECK_AGENT_STATUS,
      key: email,
      request: () => requestGetAgentStatusByEmail(email),
    })
  }
}

export function toggleCustomerView(customerView?: boolean) {
  return (dispatch, getState) => {
    const state = getState() as App.State

    dispatch({
      type: SET_CUSTOMER_VIEW_TOGGLE,
      value: customerView ?? !state.agentHub.customerView,
    })
  }
}

export function onboardAgency(agencyDetails: SetAgencyDetailsParams) {
  return {
    type: API_CALL,
    api: ONBOARD_AGENCY,
    request: async() => {
      await requestPostSetAgencyDetails(agencyDetails.agentId, agencyDetails)
      const updatedAccount = await requestPostMarkAgentAsOnboarded(agencyDetails.agentId)
      return updatedAccount
    },
  }
}
